import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import CollapsibleSummary from "./CollapsibleSummary";
import BriefAnalysis from "./BriefAnalysis";
import { docBrief, fitBrief } from "./briefAnalysis";
import {
  Upload, FileText, Download, RefreshCw, ShieldCheck, AlertTriangle,
  HelpCircle, Sparkles, History, User as UserIcon, MessageSquare, Send,
} from "lucide-react";

/*
 * Hiring Module Phase D — Candidate profile panels (2026-09-06).
 *
 * Renders three composed sections on the candidate detail page:
 *   1) FitCard        — plain-language verdict + reason
 *   2) AttachmentsPanel — resume upload + per-stage transcript upload
 *   3) AnalysisHistoryPanel — newest-first audit trail
 *
 * All three read from the same `candidate` prop (already fetched
 * by the parent) + a polling loop for the analysis-history endpoint.
 *
 * When a file lands successfully, we optimistically flip the fit
 * card into "analyzing…" mode and poll /analysis-history every 5s
 * until the newest run's status is not "running", then trigger the
 * parent to reload the candidate so verdict/reason refresh.
 */

const ACCEPT_MIME =
  "application/pdf," +
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const ACCEPT_LABEL = "PDF or DOCX, 10MB max";
const MAX_BYTES = 10 * 1024 * 1024;

const VERDICT_META = {
  strong:   { label: "Strong fit",   color: "text-[#166534] bg-[#dcfce7] border-[#86efac]", icon: ShieldCheck },
  moderate: { label: "Moderate fit", color: "text-[#1e40af] bg-[#dbeafe] border-[#93c5fd]", icon: Sparkles },
  weak:     { label: "Weak fit",     color: "text-[#991b1b] bg-[#fee2e2] border-[#f87171]", icon: AlertTriangle },
  unclear:  { label: "Not yet clear", color: "text-[#4b5563] bg-[#f3f4f6] border-[#e5e7eb]", icon: HelpCircle },
};

const TRIGGER_LABELS = {
  intake_submit:     "Intake submitted",
  resume_upload:     "Resume uploaded",
  transcript_upload: "Transcript uploaded",
  manual_rescore:    "Manual rescore",
};


function SectionCard({ title, icon: Icon, testId, right, children }) {
  return (
    <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#e5e7eb] bg-[#f9fafb]">
        <div className="flex items-center gap-2 text-[#0e1420] font-semibold">
          {Icon && <Icon className="w-4 h-4 text-[#4b5563]" />}
          {title}
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}


// ---------- FitCard ----------

export function FitCard({ candidate, analyzing }) {
  const fs = candidate?.fit_summary;
  // "Analyzing" mode overrides everything else — the LLM is churning
  // and the verdict on disk is either stale or a placeholder.
  const status = analyzing ? "running" : (fs?.status || "not_yet");
  const verdict = fs?.verdict || "unclear";
  const meta = VERDICT_META[verdict] || VERDICT_META.unclear;
  const Icon = meta.icon;
  return (
    <SectionCard title="Likelihood of Fit" icon={Sparkles} testId="hiring-candidate-fit-card">
      {status === "running" && (
        <div className="text-sm text-[#4b5563] flex items-center gap-2" data-testid="hiring-fit-analyzing">
          <RefreshCw className="w-4 h-4 animate-spin" />
          AI is analyzing… the verdict will refresh automatically once scoring completes.
        </div>
      )}
      {status !== "running" && !fs && (
        <div className="text-sm text-[#4b5563]" data-testid="hiring-fit-not-yet">
          Not yet assessed. Upload a resume or interview transcript to trigger AI analysis.
        </div>
      )}
      {status !== "running" && fs && (
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${meta.color}`}
              data-testid="hiring-fit-verdict"
            >
              <Icon className="w-4 h-4" />
              {meta.label}
            </span>
            {typeof fs.score === "number" && (
              <span className="text-xs text-[#9ca3af]" data-testid="hiring-fit-score">
                score {fs.score.toFixed(1)} / 5
              </span>
            )}
            {fs.status === "stale" && (
              <span className="text-xs text-[#92400e] bg-[#fef3c7] border border-[#fbbf24] rounded-full px-2 py-0.5">
                Stale — last run failed
              </span>
            )}
          </div>
          {fs.reason && (
            <p
              className="mt-3 text-sm text-[#0e1420] leading-relaxed"
              data-testid="hiring-fit-reason"
            >
              <CollapsibleSummary text={fs.reason} testId="hiring-fit-reason-text" />
            </p>
          )}
          {fs.updated_at && (
            <div className="mt-3 text-xs text-[#9ca3af]">
              Updated {new Date(fs.updated_at).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}


// ---------- Upload helpers ----------

function validateFile(file) {
  if (!file) return "No file selected.";
  const okType = ACCEPT_MIME.split(",").includes(file.type);
  if (!okType) return `Only PDF or DOCX files are accepted. Got ${file.type || "unknown type"}.`;
  if (file.size > MAX_BYTES) return `File is ${(file.size / 1024 / 1024).toFixed(1)}MB — 10MB is the max.`;
  if (file.size === 0) return "That file is empty — refusing to upload.";
  return null;
}

function UploadButton({ candidateId, kind, stage, disabled, onUploaded, label, testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so the same filename can be re-picked
    if (!file) return;
    const err = validateFile(file);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const url = kind === "resume"
        ? `/hiring/candidates/${candidateId}/resume`
        : `/hiring/candidates/${candidateId}/transcripts?stage=${stage}`;
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name} — AI re-analysis kicked off.`);
      onUploaded?.(data.attachment);
    } catch (ex) {
      const detail = ex.response?.data?.detail || ex.message || "Upload failed.";
      toast.error(String(detail).slice(0, 240));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_MIME}
        onChange={handleFile}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <Button
        size="sm"
        variant="outline"
        disabled={busy || disabled}
        onClick={() => inputRef.current?.click()}
        data-testid={testId}
      >
        {busy
          ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          : <Upload className="w-3.5 h-3.5 mr-1.5" />}
        {busy ? "Uploading…" : label}
      </Button>
    </>
  );
}


// ---------- AttachmentsPanel ----------

export function AttachmentsPanel({ candidate, canWrite, onUploaded, onNoteAdded }) {
  const attachments = (candidate?.attachments || []).filter((a) => !a.is_deleted);
  const cid = candidate?.id;

  async function handleDownload(a) {
    try {
      const resp = await api.get(
        `/hiring/candidates/${cid}/attachments/${a.id}/download`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: a.content_type }));
      const link = document.createElement("a");
      link.href = url;
      link.download = a.original_filename || "download";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Download failed.");
    }
  }

  async function handleDelete(a) {
    if (!window.confirm(`Remove ${a.original_filename}? The file will be soft-deleted (hidden from the profile + AI scoring) but bytes remain in cold storage.`)) {
      return;
    }
    try {
      await api.delete(`/hiring/candidates/${cid}/attachments/${a.id}`);
      toast.success("Attachment removed.");
      onUploaded?.();  // reuse the same refresh callback
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Delete failed.");
    }
  }

  const transcripts = attachments.filter((a) => a.kind === "transcript");
  const resumes = attachments.filter((a) => a.kind === "resume");

  return (
    <SectionCard title="Resume & transcripts" icon={FileText} testId="hiring-candidate-attachments">
      {/* Resume row */}
      <div className="mb-4 pb-4 border-b border-[#e5e7eb]">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-[#0e1420]">Resume</div>
          {canWrite && (
            <UploadButton
              candidateId={cid}
              kind="resume"
              onUploaded={onUploaded}
              label={resumes.length ? "Replace" : "Upload resume"}
              testId="hiring-upload-resume-btn"
            />
          )}
        </div>
        <div className="text-xs text-[#9ca3af] mb-2">{ACCEPT_LABEL}</div>
        {resumes.length === 0 && (
          <div className="text-sm text-[#4b5563]" data-testid="hiring-resume-empty">
            No resume on file yet.
          </div>
        )}
        {resumes.map((a) => (
          <AttachmentRow
            key={a.id}
            att={a}
            onDownload={() => handleDownload(a)}
            onDelete={canWrite ? () => handleDelete(a) : undefined}
            testId={`hiring-attachment-${a.id}`}
            fit={candidate?.fit_summary}
          />
        ))}
        {resumes.length > 0 && resumes[0].ai_summary && (
          <div className="mt-2 text-xs text-[#0e1420] bg-white border border-[#e5e7eb] rounded p-2 leading-relaxed"
               data-testid="hiring-resume-summary">
            <span className="font-medium text-[#4b5563]">AI summary</span>
            <BriefAnalysis brief={docBrief(candidate?.fit_summary, resumes[0].ai_summary)} testId="hiring-resume-summary-text" />
          </div>
        )}
      </div>

      {/* Per-stage transcript rows */}
      <div className="text-sm font-medium text-[#0e1420] mb-2">Interview transcripts & notes</div>
      <div className="text-xs text-[#9ca3af] mb-3">Upload one transcript per stage as interviews complete. Add notes any time. {ACCEPT_LABEL}.</div>
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((stage) => {
          const stageTranscripts = transcripts.filter((t) => t.stage_key === `stage_${stage}`);
          const stageNotes = (candidate?.notes || []).filter(
            (n) => Number(n.stage) === stage && n.kind !== "borderline",
          ).sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
          return (
            <div
              key={stage}
              className="p-3 rounded border border-[#e5e7eb] bg-[#f9fafb]"
              data-testid={`hiring-transcript-row-stage-${stage}`}
            >
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <div className="text-sm font-medium text-[#0e1420] w-16">Stage {stage}</div>
                <div className="flex-1 min-w-0">
                  {stageTranscripts.length === 0 && stageNotes.length === 0 && (
                    <div className="text-xs text-[#9ca3af]">No transcript or notes yet.</div>
                  )}
                </div>
                {canWrite && (
                  <UploadButton
                    candidateId={cid}
                    kind="transcript"
                    stage={stage}
                    onUploaded={onUploaded}
                    label={stageTranscripts.length ? "Add another" : "Upload transcript"}
                    testId={`hiring-upload-transcript-stage-${stage}-btn`}
                  />
                )}
              </div>
              {stageTranscripts.map((a) => (
                <AttachmentRow
                  key={a.id}
                  att={a}
                  onDownload={() => handleDownload(a)}
                  onDelete={canWrite ? () => handleDelete(a) : undefined}
                  testId={`hiring-attachment-${a.id}`}
                  fit={candidate?.fit_summary}
                />
              ))}
              {stageNotes.map((n) => (
                <NoteRow key={n.id} note={n} testId={`hiring-note-${n.id}`} />
              ))}
              {canWrite && (
                <AddNoteInline
                  candidateId={cid}
                  stage={stage}
                  onAdded={onNoteAdded}
                  testId={`hiring-add-note-stage-${stage}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}


function NoteRow({ note, testId }) {
  return (
    <div
      className="mt-2 flex items-start gap-2 p-2 rounded bg-white border border-[#e5e7eb]"
      data-testid={testId}
    >
      <MessageSquare className="w-3.5 h-3.5 text-[#4b5563] flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#0e1420] whitespace-pre-wrap">{note.body}</div>
        <div className="text-xs text-[#9ca3af] mt-0.5">
          {note.author_email || "unknown"} · {new Date(note.created_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
}


function AddNoteInline({ candidateId, stage, onAdded, testId }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit() {
    const trimmed = body.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/hiring/candidates/${candidateId}/notes`,
        { stage, body: trimmed },
      );
      toast.success("Note added.");
      setBody("");
      setOpen(false);
      onAdded?.(data.note);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to save note.");
    } finally {
      setBusy(false);
    }
  }
  if (!open) {
    return (
      <div className="mt-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setOpen(true)}
          data-testid={`${testId}-open`}
          className="text-xs"
        >
          <MessageSquare className="w-3 h-3 mr-1.5" /> Add interview note
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-2 space-y-2">
      <Textarea
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Your notes from this interview (immutable — corrections are new notes)…"
        data-testid={`${testId}-textarea`}
      />
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setBody(""); }}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={busy || !body.trim()}
          onClick={submit}
          data-testid={`${testId}-submit`}
        >
          {busy
            ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            : <Send className="w-3.5 h-3.5 mr-1.5" />}
          Save note
        </Button>
      </div>
    </div>
  );
}


// ---------- FullCandidateSummary ----------

export function FullCandidateSummary({ candidateId, refreshKey }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/hiring/candidates/${candidateId}/full-context`);
      setCtx(data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load full summary.");
    } finally {
      setLoading(false);
    }
  }, [candidateId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const fs = ctx?.fit_summary;
  const verdictMeta = fs?.verdict ? VERDICT_META[fs.verdict] : null;

  return (
    <SectionCard
      title="Full Candidate Summary"
      icon={UserIcon}
      testId="hiring-candidate-full-summary"
      right={(
        <Button size="sm" variant="ghost" onClick={load} data-testid="hiring-full-summary-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      )}
    >
      {err && (
        <div className="text-sm text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-3">
          {err}
        </div>
      )}
      {!err && ctx && (
        <div className="space-y-5 text-sm">
          {/* Fit summary */}
          {fs && verdictMeta && fs.status === "current" && (
            <div data-testid="hiring-full-summary-fit">
              <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1.5">AI likelihood of fit</div>
              <BriefAnalysis brief={fitBrief(fs)} testId="hiring-full-summary-fit-reason" />
            </div>
          )}
          {/* Stage-by-stage history */}
          <div data-testid="hiring-full-summary-stages">
            <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-2">Stage-by-stage history</div>
            <ol className="space-y-3">
              {(ctx.stages || []).map((s) => (
                <li
                  key={s.stage}
                  className="border-l-2 border-[#e5e7eb] pl-3"
                  data-testid={`hiring-full-summary-stage-${s.stage}`}
                >
                  <div className="text-[#0e1420] font-medium">{s.label}</div>
                  <div className="text-xs text-[#4b5563]">
                    {s.entered_at ? `Entered ${new Date(s.entered_at).toLocaleString()}` : "Not yet entered"}
                    {s.entered_by?.length ? ` · owner: ${s.entered_by.join(", ")}` : ""}
                  </div>
                  {s.transcript && (
                    <div className="mt-1 text-xs text-[#0e1420]">
                      <span className="font-medium">Transcript summary: </span>
                      {(s.transcript.ai_summary || "").trim()
                        ? <BriefAnalysis brief={docBrief(fs, s.transcript.ai_summary)} testId={`hiring-full-summary-stage-${s.stage}-transcript`} />
                        : <span className="italic text-[#4b5563]">Uploaded ({s.transcript.filename}), summary pending.</span>}
                    </div>
                  )}
                  {(s.notes || []).map((n) => (
                    <div key={n.id} className="mt-1 text-xs">
                      <span className="font-medium text-[#0e1420]">Note from {n.author_email || "unknown"}: </span>
                      <span className="text-[#0e1420] whitespace-pre-wrap">{n.body}</span>
                    </div>
                  ))}
                </li>
              ))}
            </ol>
          </div>
          {/* Resume */}
          {ctx.resume && (
            <div data-testid="hiring-full-summary-resume">
              <div className="text-xs uppercase tracking-wide text-[#4b5563] mb-1">Resume</div>
              {(ctx.resume.ai_summary || "").trim() ? (
                <p className="text-[#0e1420] leading-relaxed">
                  <BriefAnalysis brief={docBrief(fs, ctx.resume.ai_summary)} testId="hiring-full-summary-resume-text" />
                </p>
              ) : (
                <p className="text-xs italic text-[#4b5563]">Uploaded ({ctx.resume.filename}), summary pending.</p>
              )}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}


function AttachmentRow({ att, onDownload, onDelete, testId, compact, fit }) {
  return (
    <div
      className={`${compact ? "" : "mt-2 p-2 rounded border border-[#e5e7eb] bg-[#f9fafb]"}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3">
        <FileText className="w-4 h-4 text-[#4b5563] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#0e1420] truncate">{att.original_filename}</div>
          <div className="text-xs text-[#9ca3af]">
            {(att.size / 1024).toFixed(1)} KB · uploaded {new Date(att.uploaded_at).toLocaleString()}
            {att.uploaded_by && <> · by {att.uploaded_by}</>}
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDownload}
          data-testid={`${testId}-download`}
        >
          <Download className="w-3.5 h-3.5" />
        </Button>
        {onDelete && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            data-testid={`${testId}-delete`}
            className="text-[#7a1c1c] hover:bg-[#fff5f5]"
            aria-label="Remove attachment"
          >
            <span className="text-[13px]">×</span>
          </Button>
        )}
      </div>
      {att.ai_summary && (
        <div className="mt-2 pt-2 border-t border-[#e5e7eb] text-xs text-[#0e1420] leading-relaxed"
             data-testid={`${testId}-summary`}>
          <span className="font-medium text-[#4b5563]">AI summary</span>
          <BriefAnalysis brief={docBrief(fit, att.ai_summary)} testId={`${testId}-summary-text`} />
        </div>
      )}
    </div>
  );
}


// ---------- AnalysisHistoryPanel ----------

export function AnalysisHistoryPanel({ candidateId, refreshKey }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/hiring/candidates/${candidateId}/analysis-history`);
      setRuns(data?.runs || []);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load history.");
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <SectionCard
      title="AI analysis history"
      icon={History}
      testId="hiring-candidate-analysis-history"
      right={(
        <Button size="sm" variant="ghost" onClick={load} data-testid="hiring-analysis-history-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      )}
    >
      {loading && runs.length === 0 && (
        <div className="text-sm text-[#4b5563]">Loading…</div>
      )}
      {err && (
        <div className="text-sm text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-3">
          {err}
        </div>
      )}
      {!loading && !err && runs.length === 0 && (
        <div className="text-sm text-[#4b5563]" data-testid="hiring-analysis-history-empty">
          No runs yet.
        </div>
      )}
      {runs.length > 0 && (
        <ol className="space-y-2">
          {runs.map((r) => {
            const kind = r.triggered_by?.kind;
            const label = TRIGGER_LABELS[kind] || kind || "Run";
            const started = new Date(r.started_at);
            const completed = r.completed_at ? new Date(r.completed_at) : null;
            const durationMs = completed ? completed - started : null;
            const durationLabel = durationMs != null
              ? `${(durationMs / 1000).toFixed(1)}s`
              : "in progress";
            const verdict = r.fit_summary_snapshot?.verdict;
            const verdictMeta = verdict ? VERDICT_META[verdict] : null;
            return (
              <li
                key={r.id}
                className="flex items-start gap-3 p-3 rounded border border-[#e5e7eb] bg-[#f9fafb]"
                data-testid={`hiring-analysis-run-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0e1420] flex items-center gap-2 flex-wrap">
                    {label}
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border ${
                        r.status === "success" ? "text-[#166534] bg-[#dcfce7] border-[#86efac]"
                          : r.status === "error" ? "text-[#991b1b] bg-[#fee2e2] border-[#f87171]"
                          : "text-[#4b5563] bg-[#f3f4f6] border-[#e5e7eb]"
                      }`}
                      data-testid={`hiring-analysis-run-status-${r.id}`}
                    >
                      {r.status}
                    </span>
                    {verdictMeta && (
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${verdictMeta.color}`}>
                        {verdictMeta.label}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#4b5563] mt-1">
                    {started.toLocaleString()} · {durationLabel}
                    {r.triggered_by?.actor_email && <> · by {r.triggered_by.actor_email}</>}
                  </div>
                  {r.error && (
                    <div className="text-xs text-[#991b1b] mt-1 italic">
                      {r.error}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}


/*
 * Convenience hook: after an upload lands, poll /analysis-history
 * every 5s until the NEWEST run's status flips out of "running",
 * then call `onRefresh` so the parent reloads candidate (fit_summary).
 *
 * Bail after 12 polls (~60s) so a hung backend can't spin forever.
 */
export function useAnalysisPolling({ candidateId, onRefresh }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const timerRef = useRef(null);
  const attemptsRef = useRef(0);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setAnalyzing(false);
    attemptsRef.current = 0;
  }, []);

  const start = useCallback(() => {
    stop();
    setAnalyzing(true);
    const tick = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/hiring/candidates/${candidateId}/analysis-history`);
        const newest = (data?.runs || [])[0];
        setRefreshKey((k) => k + 1);
        if (newest && newest.status !== "running") {
          stop();
          onRefresh?.();
          if (newest.status === "success") {
            toast.success("Fit summary updated.");
          } else if (newest.status === "error") {
            toast.error(`Analysis failed: ${newest.error || "unknown"}`);
          }
          return;
        }
      } catch (e) {
        // Non-fatal — keep polling; a transient 5xx shouldn't kill the loop.
      }
      if (attemptsRef.current >= 12) {
        stop();
        toast("Still analyzing… refresh in a moment to see the latest.");
        return;
      }
      timerRef.current = setTimeout(tick, 5000);
    };
    // First tick fires immediately so the UI feels responsive.
    timerRef.current = setTimeout(tick, 1500);
  }, [candidateId, onRefresh, stop]);

  // Clean up if the component unmounts mid-poll.
  useEffect(() => () => stop(), [stop]);

  return { analyzing, refreshKey, start };
}
